/**
 * Export Hub - offline export.
 * Dashboard: PDF or PPTX (DOM chart capture, 1 chart per page/slide)
 * Table: XLSX, PDF, or PPTX (data tables)
 */
import React, { useState, useCallback, useRef, Suspense } from 'react';
import { Download } from 'lucide-react';
import {
  captureDashboardSections,
  exportDashboardToPdf,
  exportTableToXlsx,
  exportTableToPdf,
  exportWorkbookToXlsx,
  isExportDashboardStillLoadingError,
} from '../services/exportService';
import {
  exportDashboardSummaryToPptx,
  exportTableToPptx,
  pptxSlideBudgetDisclosure,
  sanitizeDownloadFilename,
} from '../services/pptxExport';
import {
  KPOA_EXPORT_FETCH_CAP,
  prepareExportPayload,
} from '../services/exportPayloadPrep';
import { NIDATA_LOADING_LABEL } from '../lib/nidataUiCopy';
import { useIncidentDashboardPdfExport } from '../contexts/IncidentDashboardPdfExportContext';
import {
  fetchMonthlyReportingData,
  pooledMonthlyCompletenessPct,
  type ReportingAggregateResult,
} from '../services/dashboardData';
import { fetchEthicData } from '../services/ethicData';
import { fetchKpoaIncidentData } from '../services/kpoaIncidentData';
import { fetchHrImportDashboardBundle } from '../services/hrImportData';
import {
  type ReportingMonth,
  buddhistFiscalYear,
  fiscalQuarterOptionLabel,
  fiscalYearQuarterToAnchorReportingMonth,
  reportingMonthToFiscalYearQuarter,
  type FiscalQuarter,
} from '../lib/reportingMonth';
import { formatThaiMonthYYBuddhist } from '../utils/dateThai';
import { useDepartments } from '../contexts/useDepartments';
import { useComplianceCategories } from '../hooks/useComplianceCategories';
import { DEFAULT_COMPLIANCE_CATEGORIES } from '../lib/complianceCategories';
import { resolveScopedDepartments } from '../lib/departments';
import {
  buildDepartmentIdToNameMap,
  filterDepartmentIdsForUi,
  resolveDepartmentDisplayName,
} from '../lib/departmentUi';
import { useSupervisorReportingFilters } from '../contexts/SupervisorReportingFiltersContext';
import type { RoleScope } from '../services/auth/roleResolver';
import type { RoleContext } from '../lib/roles';
import type { ReportingScopeOverrides, ReportingFetchOptions } from '../services/dashboardData';
import {
  METRICS_KEY_CHART1_CONSISTENCY_PCT,
  METRICS_KEY_OVERALL_CONSISTENCY_PCT,
} from '../lib/submissionMetrics';

import { safeLazyImport } from '../utils/safeLazyImport';
import { buildEthicExportData } from '../utils/ethicExportRows';
import { reportingMonthToFiscalYear } from '../utils/incidentReportsExport';
import { buildKpoaExportData, filterKpoaRowsByDateRange } from '../utils/kpoaExportRows';
import { buildHrExportPdfData } from '../utils/hrImportExportRows';
import { getExportPeriodContext, type ExportPeriod } from '../utils/exportPeriod';

const Dashboard = React.lazy(safeLazyImport(() => import('./Dashboard')));
const KpoaIncidentDashboard = React.lazy(() =>
  import('./KpoaIncidentDashboard').then((mod) => ({ default: mod.KpoaIncidentDashboard })),
);
const HrImportDashboard = React.lazy(() =>
  import('./hr-import/HrImportDashboard').then((mod) => ({ default: mod.HrImportDashboard })),
);

const QUESTIONS = [
  { label: 'ข้อ1', scoreKey: 'question_1_score', countKey: 'question_1_total', fallbackScoreKey: 'chart_1_score', fallbackCountKey: 'chart_1_total' },
  { label: 'ข้อ2', scoreKey: 'question_2_score', countKey: 'question_2_total', fallbackScoreKey: 'chart_2_score', fallbackCountKey: 'chart_2_total' },
  { label: 'ข้อ3', scoreKey: 'question_3_score', countKey: 'question_3_total', fallbackScoreKey: 'chart_3_score', fallbackCountKey: 'chart_3_total' },
  { label: 'ข้อ4', scoreKey: 'question_4_score', countKey: 'question_4_total', fallbackScoreKey: 'chart_4_score', fallbackCountKey: 'chart_4_total' },
  { label: 'ข้อ5', scoreKey: 'question_5_score', countKey: 'question_5_total', fallbackScoreKey: 'chart_5_score', fallbackCountKey: 'chart_5_total' },
  { label: 'ข้อ6', scoreKey: 'question_6_score', countKey: 'question_6_total', fallbackScoreKey: 'chart_6_score', fallbackCountKey: 'chart_6_total' },
  { label: 'ข้อ7', scoreKey: 'question_7_score', countKey: 'question_7_total', fallbackScoreKey: 'chart_7_score', fallbackCountKey: 'chart_7_total' },
  { label: 'ข้อ8', scoreKey: 'question_8_score', countKey: 'question_8_total', fallbackScoreKey: 'chart_8_score', fallbackCountKey: 'chart_8_total' },
  { label: 'ข้อ9', scoreKey: 'question_9_score', countKey: 'question_9_total', fallbackScoreKey: 'chart_9_score', fallbackCountKey: 'chart_9_total' },
];

const EXTRA = {
  diseaseScoreKey: 'disease_monthly_avg_score',
};

type ExportWhat = 'dashboard' | 'table';
type ExportFormat = 'pdf' | 'xlsx' | 'pptx';

const PERIOD_LABELS: Record<ExportPeriod, string> = {
  monthly: 'รายเดือน',
  trimester: 'รายไตรมาส',
  year: 'รายปี',
};

const REPORTING_SHELL_CATEGORY_IDS = new Set(['nurse_notes', 'ethicSurveys', 'med_error', 'siren_bundle', 'needleandsharp', 'med_error_incident', 'fall_incident']);
const EXPORT_ONLY_CATEGORY_IDS = new Set(['kpoa', 'hr']);
const DASHBOARD_EXPORT_STABLE_MS = 120_000;
const DASHBOARD_EXPORT_LOADING_RETRIES = 5;
const PPTX_HINT_ID = 'export-hub-pptx-hint';

function formatExportError(error: unknown): string {
  const code = typeof (error as { code?: unknown })?.code === 'string' ? (error as { code: string }).code : '';
  const message =
    error instanceof Error
      ? error.message
      : typeof (error as { message?: unknown })?.message === 'string'
        ? (error as { message: string }).message
        : '';
  const text = `${code} ${message}`.toLowerCase();
  if (text.includes('permission-denied') || text.includes('missing or insufficient permissions')) {
    return 'ไม่มีสิทธิ์อ่านข้อมูลสำหรับการส่งออกนี้ กรุณาตรวจสอบขอบเขตหน่วยงานหรือแจ้งผู้ดูแลระบบ';
  }
  if (text.includes('unsupported color function') || text.includes('oklch')) {
    return 'ส่งออก PDF ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง';
  }
  return message || 'ส่งออกไม่สำเร็จ';
}

export type ExportHubProps = {
  roleScope: RoleScope | null;
  roleContext: RoleContext | null;
  reportingScope?: ReportingScopeOverrides;
  reportingMonth: ReportingMonth;
  onReportingMonthChange?: (m: ReportingMonth) => void;
  reportingOptions?: ReportingFetchOptions;
};

function buildNurseNotesExportData(
  yearlyResults: Array<{ reportingMonth: ReportingMonth; result: ReportingAggregateResult }>,
  months: ReportingMonth[],
): { headers: string[]; rows: string[][] } {
  const resultMap = new Map(yearlyResults.map((e) => [e.reportingMonth, e.result]));

  const headers = [
    'ลำดับ',
    'เดือน',
    ...QUESTIONS.flatMap((q) => [`${q.label} คะแนน`, `${q.label} จำนวน`]),
    'ร้อยละความสมบูรณ์',
    'คะแนนรายโรค',
    'ความสอดคล้อง',
  ];

  const formatScore = (v: number | null | undefined) =>
    v != null && Number.isFinite(v) ? v.toFixed(2) : '-';
  const formatCount = (v: number | null | undefined) =>
    v != null && Number.isFinite(v) ? String(Math.round(v)) : '-';

  const rows = months.map((reportingMonth, index) => {
    const result = resultMap.get(reportingMonth);
    const scores = QUESTIONS.map((q) => {
      const avg = result?.averages;
      return (avg?.[q.scoreKey as keyof typeof avg] ?? avg?.[(q as { fallbackScoreKey?: string }).fallbackScoreKey ?? ''] ?? null) as number | null;
    });
    const counts = QUESTIONS.map((q) => {
      const tot = result?.totals;
      return (tot?.[q.countKey as keyof typeof tot] ?? tot?.[(q as { fallbackCountKey?: string }).fallbackCountKey ?? ''] ?? null) as number | null;
    });
    const monthlyCompletenessPct = pooledMonthlyCompletenessPct(result);
    const diseaseScore =
      result?.averages[EXTRA.diseaseScoreKey] != null &&
      Number.isFinite(result.averages[EXTRA.diseaseScoreKey])
        ? result.averages[EXTRA.diseaseScoreKey]
        : null;
    const consistencyPct =
      result?.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT] != null &&
      Number.isFinite(result.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT])
        ? result.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT]
        : result?.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT] != null &&
            Number.isFinite(result.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT])
          ? result.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT]
          : null;

    return [
      String(index + 1),
      formatThaiMonthYYBuddhist(reportingMonth),
      ...scores.flatMap((s) => formatScore(s)),
      ...counts.flatMap((c) => formatCount(c)),
      monthlyCompletenessPct != null ? `${monthlyCompletenessPct.toFixed(2)}%` : '-',
      formatScore(diseaseScore),
      consistencyPct != null ? `${consistencyPct.toFixed(2)}%` : '-',
    ];
  });

  return { headers, rows };
}

export const ExportHub: React.FC<ExportHubProps> = ({
  roleScope,
  roleContext,
  reportingScope,
  reportingMonth,
  onReportingMonthChange,
  reportingOptions,
}) => {
  const { departments } = useDepartments();
  const departmentIdToName = React.useMemo(() => buildDepartmentIdToNameMap(departments), [departments]);
  const departmentScopeLabel = React.useCallback(
    (ids: string[]) =>
      filterDepartmentIdsForUi(ids)
        .map((id) => resolveDepartmentDisplayName(id, undefined, departmentIdToName))
        .join(', '),
    [departmentIdToName],
  );
  useComplianceCategories();
  const sharedFilters = useSupervisorReportingFilters();

  const [what, setWhat] = useState<ExportWhat>('table');
  const [categoryId, setCategoryId] = useState<string>('nurse_notes');
  const [period, setPeriod] = useState<ExportPeriod>('year');
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [selectedMonth, setSelectedMonth] = useState<ReportingMonth>(reportingMonth);
  const [slideBudgetHint, setSlideBudgetHint] = useState<string | null>(null);

  const effectiveCategoryId = EXPORT_ONLY_CATEGORY_IDS.has(categoryId)
    ? categoryId
    : sharedFilters?.categoryId ?? categoryId;
  const effectiveReportingMonth = sharedFilters?.reportingMonth ?? selectedMonth;

  React.useEffect(() => {
    if (!sharedFilters) setSelectedMonth(reportingMonth);
  }, [reportingMonth, sharedFilters]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfOrientation, setPdfOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const dashboardRef = useRef<HTMLDivElement>(null);
  const { getMedErrorRoot, getFallRoot } = useIncidentDashboardPdfExport();

  const categoryOptions = React.useMemo(() => {
    const nursing = DEFAULT_COMPLIANCE_CATEGORIES.find(
      (c) => c.id === 'nurse_notes' || c.title === 'บันทึกทางการพยาบาล',
    );
    const ethic = DEFAULT_COMPLIANCE_CATEGORIES.find(
      (c) => c.id === 'ethicSurveys' || c.title === 'บรรยากาศองค์กรจริยธรรม',
    );
    const siren = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'siren_bundle');
    const needle = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'needleandsharp');
    const medErrorIncident = DEFAULT_COMPLIANCE_CATEGORIES.find(
      (c) => c.id === 'med_error_incident' || c.title === 'รายงานอุบัติการณ์ความคลาดเคลื่อนทางยา',
    );
    const kpoa = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'kpoa');
    const hr = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'hr');
    const out: Array<{ id: string; title: string }> = [];
    if (nursing) out.push({ id: nursing.id, title: nursing.title });
    if (ethic) out.push({ id: ethic.id, title: ethic.title });
    if (siren) out.push({ id: siren.id, title: siren.title });
    if (needle) out.push({ id: needle.id, title: needle.title });
    if (medErrorIncident) out.push({ id: medErrorIncident.id, title: medErrorIncident.title });
    const fallIncident = DEFAULT_COMPLIANCE_CATEGORIES.find(
      (c) => c.id === 'fall_incident' || c.title === 'จำนวนอุบัติการณ์การพลัดตกหกล้ม',
    );
    if (fallIncident) out.push({ id: fallIncident.id, title: fallIncident.title });
    if (kpoa) out.push({ id: kpoa.id, title: kpoa.title });
    if (hr) out.push({ id: hr.id, title: hr.title });
    return out;
  }, []);

  const isEthic = effectiveCategoryId === 'ethicSurveys';
  const isMedErrorIncident = effectiveCategoryId === 'med_error_incident';
  const isFallIncident = effectiveCategoryId === 'fall_incident';
  const isKpoa = effectiveCategoryId === 'kpoa';
  const isHr = effectiveCategoryId === 'hr';

  const scopedDepartments = React.useMemo(
    () =>
      resolveScopedDepartments(
        departments,
        roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? [],
        roleContext?.lineOfWorkDepartmentIds ?? [],
      ),
    [departments, roleScope, roleContext],
  );

  const effectiveReportingScope = React.useMemo((): ReportingScopeOverrides | undefined => {
    if (reportingScope) return reportingScope;
    const deptIds = roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? [];
    if (deptIds.length) return { departmentIds: deptIds };
    return undefined;
  }, [reportingScope, roleScope]);

  const scopedExportDepartmentIds = React.useMemo(() => {
    const explicitDeptIds =
      effectiveReportingScope?.departmentIds ??
      roleScope?.view?.departmentIds ??
      roleScope?.departmentIds ??
      [];
    if (explicitDeptIds.length > 0) return explicitDeptIds;
    return scopedDepartments.map((department) => department.id);
  }, [effectiveReportingScope, roleScope, scopedDepartments]);

  const resolveScopeLabel = useCallback((): string => {
    if (isMedErrorIncident) return 'ทุกหน่วยงาน';
    if (isFallIncident) return 'ทุกหน่วยงาน (ข้อมูลนำเข้า)';
    if (isHr) return 'ข้อมูลทรัพยากรบุคคลเป็นภาพรวมตามชุดนำเข้าที่ใช้งานอยู่';
    const ids = scopedExportDepartmentIds;
    if (ids.includes('*')) return 'ทุกหน่วยงาน';
    if (ids.length > 0) return departmentScopeLabel(ids);
    const scopeDeptIds = effectiveReportingScope?.departmentIds ?? [];
    if (scopeDeptIds.includes('*')) return 'ทุกหน่วยงาน';
    if (scopeDeptIds.length > 0) return departmentScopeLabel(scopeDeptIds);
    return 'ตามสิทธิ์';
  }, [
    isMedErrorIncident,
    isFallIncident,
    isHr,
    scopedExportDepartmentIds,
    departmentScopeLabel,
    effectiveReportingScope,
  ]);

  const handleExport = useCallback(async () => {
    setError(null);
    setSlideBudgetHint(null);
    setExporting(true);

    try {
      const categoryLabel =
        categoryOptions.find((c) => c.id === effectiveCategoryId)?.title ?? effectiveCategoryId;
      const periodLabel = PERIOD_LABELS[period];
      const scopeLabel = resolveScopeLabel();

      // 1) PPTX
      if (format === 'pptx') {
        // Dashboard PPTX = live chart images (same DOM clone as PDF), not table dump.
        if (what === 'dashboard') {
          const el = dashboardRef.current;
          if (!el) {
            setError('ไม่พบองค์ประกอบแดชบอร์ดสำหรับส่งออก');
            return;
          }
          await new Promise((r) => setTimeout(r, 2500));
          let captures: Awaited<ReturnType<typeof captureDashboardSections>> = [];
          for (let attempt = 0; attempt < DASHBOARD_EXPORT_LOADING_RETRIES; attempt += 1) {
            try {
              captures = await captureDashboardSections(el, {
                waitForStableMs: DASHBOARD_EXPORT_STABLE_MS,
              });
              break;
            } catch (err) {
              if (isExportDashboardStillLoadingError(err) && attempt < DASHBOARD_EXPORT_LOADING_RETRIES - 1) {
                await new Promise((r) => setTimeout(r, 2000));
                continue;
              }
              throw err;
            }
          }

          const prep = await prepareExportPayload({
            categoryId: effectiveCategoryId,
            period,
            reportingMonth: effectiveReportingMonth,
            categoryLabel,
            periodLabel,
            scopeLabel,
            scopedExportDepartmentIds,
            departmentIdToName,
            roleScope,
            roleContext,
            reportingScope: effectiveReportingScope,
            reportingOptions,
            includeDashboardKpis: true,
          });
          if (prep.ok === false) {
            setError(prep.error);
            return;
          }
          if (prep.slideBudgetHint) setSlideBudgetHint(prep.slideBudgetHint);
          const filename = sanitizeDownloadFilename(prep.filenameBase, 'pptx');
          const chartImages = captures.map((c) => ({
            dataUrl: c.dataUrl,
            label: c.label,
            widthPx: c.widthPx,
            heightPx: c.heightPx,
            nativeChart: c.nativeChart ?? null,
          }));
          if (chartImages.length === 0) {
            setError('ไม่พบแผนภูมิในแดชบอร์ดสำหรับส่งออก');
            return;
          }
          const projected =
            1 + (prep.kpis?.length ? 1 : 0) + chartImages.length;
          const hint = prep.slideBudgetHint ?? pptxSlideBudgetDisclosure(projected);
          if (hint) setSlideBudgetHint(hint);
          await exportDashboardSummaryToPptx({
            title: prep.title,
            subtitle: `${periodLabel} · ${scopeLabel}`,
            metaRows: prep.metaRows,
            filename,
            footerLeft: prep.footerLeft,
            kpis: prep.kpis,
            chartImages,
          });
          return;
        }

        // Table PPTX — shared prep path only
        const prep = await prepareExportPayload({
          categoryId: effectiveCategoryId,
          period,
          reportingMonth: effectiveReportingMonth,
          categoryLabel,
          periodLabel,
          scopeLabel,
          scopedExportDepartmentIds,
          departmentIdToName,
          roleScope,
          roleContext,
          reportingScope: effectiveReportingScope,
          reportingOptions,
          includeDashboardKpis: false,
        });
        if (prep.ok === false) {
          setError(prep.error);
          return;
        }
        if (prep.slideBudgetHint) setSlideBudgetHint(prep.slideBudgetHint);
        const filename = sanitizeDownloadFilename(prep.filenameBase, 'pptx');
        await exportTableToPptx({
          title: prep.title,
          headers: prep.headers,
          rows: prep.rows,
          sections: prep.sections,
          metaRows: prep.metaRows,
          filename,
          footerLeft: prep.footerLeft,
        });
        return;
      }

      // 2) Dashboard PDF — DOM capture
      if (what === 'dashboard') {
        const el = dashboardRef.current;
        if (!el) {
          setError('ไม่พบองค์ประกอบแดชบอร์ดสำหรับส่งออก');
          return;
        }
        const filename = `dashboard-${categoryLabel.replace(/\s/g, '-')}-${periodLabel}-${effectiveReportingMonth}`;
        await new Promise((r) => setTimeout(r, 2500));
        for (let attempt = 0; attempt < DASHBOARD_EXPORT_LOADING_RETRIES; attempt += 1) {
          try {
            await exportDashboardToPdf(el, filename, {
              orientation: pdfOrientation,
              waitForStableMs: DASHBOARD_EXPORT_STABLE_MS,
            });
            break;
          } catch (err) {
            if (isExportDashboardStillLoadingError(err) && attempt < DASHBOARD_EXPORT_LOADING_RETRIES - 1) {
              await new Promise((r) => setTimeout(r, 2000));
              continue;
            }
            throw err;
          }
        }
        return;
      }

      // 3) Table XLSX — shared prep
      if (format === 'xlsx') {
        const prep = await prepareExportPayload({
          categoryId: effectiveCategoryId,
          period,
          reportingMonth: effectiveReportingMonth,
          categoryLabel,
          periodLabel,
          scopeLabel,
          scopedExportDepartmentIds,
          departmentIdToName,
          roleScope,
          roleContext,
          reportingScope: effectiveReportingScope,
          reportingOptions,
          includeDashboardKpis: false,
        });
        if (prep.ok === false) {
          setError(prep.error);
          return;
        }
        if (prep.workbookSheets?.length) {
          await exportWorkbookToXlsx(prep.workbookSheets, `${prep.filenameBase}.xlsx`);
        } else if (prep.headers && prep.rows) {
          const sheetRows: string[][] = [...prep.metaRows, [], prep.headers, ...prep.rows];
          await exportTableToXlsx(
            sheetRows,
            prep.singleSheetName ?? 'Sheet1',
            `${prep.filenameBase}.xlsx`,
          );
        } else {
          setError('ไม่มีข้อมูลสำหรับส่งออก');
        }
        return;
      }

      // 4) Table PDF — legacy per-category paths (med/fall = DOM; others = table PDF)
      if (isEthic) {
        const ids = scopedExportDepartmentIds.includes('*') ? ['*'] : scopedExportDepartmentIds;
        const { startDate, endDate } = getExportPeriodContext(period, effectiveReportingMonth);
        const rows = await fetchEthicData({
          departmentIds: ids,
          startDate,
          endDate,
        });
        const { headers, rows: dataRows } = buildEthicExportData(rows, departmentIdToName);
        const metaRows: string[][] = [
          ['หน่วยงาน', scopeLabel],
          ['ช่วงข้อมูล', `${startDate.toLocaleDateString('th-TH')} - ${endDate.toLocaleDateString('th-TH')}`],
        ];
        await exportTableToPdf(headers, dataRows, `ethic-${effectiveReportingMonth}.pdf`, metaRows);
      } else if (isMedErrorIncident) {
        const fiscalYear = reportingMonthToFiscalYear(effectiveReportingMonth);
        const root = getMedErrorRoot();
        if (!root) {
          setError(
            'ไม่พบแดชบอร์ดรายงานอุบัติการณ์ความคลาดเคลื่อนทางยาในหน้านี้ — เปิดหมวดรายงานความคลาดเคลื่อนทางยาให้แสดงแดชบอร์ดก่อนส่งออก PDF',
          );
          return;
        }
        await exportDashboardToPdf(root, `med-error-dashboard-${fiscalYear}.pdf`, {
          orientation: pdfOrientation,
        });
      } else if (isFallIncident) {
        const fiscalYear = reportingMonthToFiscalYear(effectiveReportingMonth);
        const root = getFallRoot();
        if (!root) {
          setError(
            'ไม่พบแดชบอร์ดการพลัดตกหกล้มในหน้านี้ — เปิดหมวดรายงานให้แสดงแดชบอร์ดก่อนส่งออก PDF',
          );
          return;
        }
        await exportDashboardToPdf(root, `fall-dashboard-${fiscalYear}.pdf`, {
          orientation: pdfOrientation,
        });
      } else if (isKpoa) {
        const ids = scopedExportDepartmentIds.includes('*') ? ['*'] : scopedExportDepartmentIds;
        const periodContext = getExportPeriodContext(period, effectiveReportingMonth);
        const { startDate, endDate, fiscalYear } = periodContext;
        const rows = await fetchKpoaIncidentData({
          limit: KPOA_EXPORT_FETCH_CAP,
          departmentIds: ids,
          preservePermissionErrors: true,
        });
        const filteredRows = filterKpoaRowsByDateRange(rows, startDate, endDate);
        const { headers: exportHeaders, rows: dataRows } = buildKpoaExportData(filteredRows, departmentIdToName);
        if (dataRows.length === 0) {
          setError('ไม่พบข้อมูล คปอ. ในช่วงเวลาหรือขอบเขตหน่วยงานที่เลือก');
          return;
        }
        const metaRows: string[][] = [
          ['หมวดหมู่', 'คปอ.'],
          ['หน่วยงาน', scopeLabel],
          ['ช่วงเวลา', periodLabel],
          ['ปีงบประมาณ', String(fiscalYear)],
          ['ช่วงข้อมูล', `${startDate.toLocaleDateString('th-TH')} - ${endDate.toLocaleDateString('th-TH')}`],
          ['จำนวนรายการ', `${dataRows.length} จากข้อมูลที่ดึงสูงสุด ${KPOA_EXPORT_FETCH_CAP} รายการ`],
          ['หมายเหตุ', 'ข้อมูล คปอ. ใช้ชุดข้อมูลล่าสุดที่ระบบอ่านได้ แล้วกรองตามช่วงเวลาที่เลือก'],
          ['คำอธิบายค่าว่าง', '— หมายถึงไม่มีข้อมูลหรือไม่เกี่ยวข้องกับรายการนั้น'],
        ];
        await exportTableToPdf(
          exportHeaders,
          dataRows,
          `kpoa-${effectiveReportingMonth}.pdf`,
          metaRows,
        );
      } else if (isHr) {
        const { fiscalYear } = getExportPeriodContext(period, effectiveReportingMonth);
        const bundle = await fetchHrImportDashboardBundle();
        if (!bundle.activeBatchId) {
          setError('ยังไม่มีชุดข้อมูลทรัพยากรบุคคลที่พร้อมส่งออก');
          return;
        }
        const metaRows: string[][] = [
          ['หมวดหมู่', 'ทรัพยากรบุคคล'],
          ['ปีงบประมาณ', String(fiscalYear)],
          ['เนื้อหา PDF', 'รวมตัวชี้วัดรายปี อัตราการลาออกรายปี และภาพรวมรายเดือนในตารางเดียว'],
          ['ขอบเขตข้อมูล', 'ข้อมูลทรัพยากรบุคคลเป็นภาพรวมตามชุดนำเข้าที่ใช้งานอยู่'],
        ];
        const { headers: exportHeaders, rows: dataRows } = buildHrExportPdfData(bundle, fiscalYear);
        await exportTableToPdf(exportHeaders, dataRows, `hr-${fiscalYear}.pdf`, metaRows);
      } else {
        const periodContext = getExportPeriodContext(period, effectiveReportingMonth);
        const months = periodContext.months;
        const results = await Promise.all(
          months.map((m) =>
            fetchMonthlyReportingData(
              m,
              effectiveCategoryId,
              roleScope ?? undefined,
              roleContext ?? undefined,
              effectiveReportingScope,
              reportingOptions,
            ),
          ),
        );
        const yearlyResults = months.map((m, i) => ({
          reportingMonth: m,
          result: { ...results[i], months },
        }));
        const { headers, rows: dataRows } = buildNurseNotesExportData(yearlyResults, months);
        const metaRows: string[][] = [
          ['หน่วยงาน', scopeLabel],
          ['ช่วงเวลา', periodLabel],
          ['ปีงบประมาณ', String(periodContext.fiscalYear)],
          ['หมวดหมู่', categoryLabel],
        ];
        await exportTableToPdf(
          headers,
          dataRows,
          `table-${effectiveCategoryId}-${effectiveReportingMonth}.pdf`,
          metaRows,
        );
      }
    } catch (err) {
      setError(formatExportError(err));
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  }, [
    what,
    effectiveCategoryId,
    period,
    format,
    effectiveReportingMonth,
    isEthic,
    isMedErrorIncident,
    isFallIncident,
    isKpoa,
    isHr,
    roleScope,
    roleContext,
    effectiveReportingScope,
    scopedExportDepartmentIds,
    reportingOptions,
    categoryOptions,
    departmentIdToName,
    pdfOrientation,
    getMedErrorRoot,
    getFallRoot,
    resolveScopeLabel,
  ]);

  const periodOptions: ExportPeriod[] = isEthic ? ['monthly', 'trimester', 'year'] : ['monthly', 'trimester', 'year'];
  const showPptxHint = what === 'dashboard' && format === 'pptx';
  const mountDashboardClone = what === 'dashboard' && (format === 'pdf' || format === 'pptx');

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-sm">
      <h2 className="mb-6 text-xl font-semibold text-slate-800">ส่งออก</h2>

      <div className="flex flex-col gap-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            สิ่งที่ต้องการส่งออก
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
              value={effectiveCategoryId}
              onChange={(e) => {
                const v = e.target.value;
                setCategoryId(v);
                if (REPORTING_SHELL_CATEGORY_IDS.has(v)) {
                  sharedFilters?.setCategoryId(v);
                }
              }}
            >
              {categoryOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.title}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            ส่วนที่จะส่งออก
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
              value={what}
              onChange={(e) => {
                const next = e.target.value as ExportWhat;
                setWhat(next);
                if (next === 'dashboard' && format === 'xlsx') {
                  setFormat('pdf');
                }
              }}
            >
              <option value="dashboard">Dashboard</option>
              <option value="table">ตารางข้อมูล</option>
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            ช่วงเวลา
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
              value={period}
              onChange={(e) => setPeriod(e.target.value as ExportPeriod)}
            >
              {periodOptions.map((p) => (
                <option key={p} value={p}>
                  {PERIOD_LABELS[p]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            รูปแบบ
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
              aria-describedby={showPptxHint ? PPTX_HINT_ID : undefined}
            >
              {what === 'dashboard' ? (
                <>
                  <option value="pdf">PDF (ภาพแดชบอร์ด)</option>
                  <option value="pptx">PowerPoint (แผนภูมิแก้ไขได้ + ภาพ)</option>
                </>
              ) : (
                <>
                  <option value="xlsx">Excel (XLSX)</option>
                  <option value="pdf">PDF</option>
                  <option value="pptx">PowerPoint (PPTX)</option>
                </>
              )}
            </select>
          </label>
        </div>

        {showPptxHint ? (
          <p id={PPTX_HINT_ID} className="text-sm text-slate-600">
            PowerPoint จะใส่แผนภูมิแดชบอร์ด แผนภูมิละ 1 สไลด์ — แท่ง/เส้น/วงกลมที่รองรับเป็นแผนภูมิแก้ไขได้ใน
            PowerPoint ส่วนแผนภูมิซับซ้อนยังเป็นภาพ
          </p>
        ) : null}

        {format === 'pdf' &&
        (what === 'dashboard' || (what === 'table' && (isMedErrorIncident || isFallIncident))) ? (
          <label className="flex max-w-md flex-col gap-1.5 text-sm font-medium text-slate-700">
            ทิศทาง PDF (A4)
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
              value={pdfOrientation}
              onChange={(e) => setPdfOrientation(e.target.value as 'portrait' | 'landscape')}
            >
              <option value="portrait">แนวตั้ง (Portrait)</option>
              <option value="landscape">แนวนอน (Landscape)</option>
            </select>
          </label>
        ) : null}

        {period === 'year' ? (
          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            ปี
            <select
              className="max-w-[140px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
              value={effectiveReportingMonth.substring(0, 4)}
              onChange={(e) => {
                const next = `${e.target.value}-01` as ReportingMonth;
                setSelectedMonth(next);
                sharedFilters?.setReportingMonth(next);
                onReportingMonthChange?.(next);
              }}
            >
              {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map(
                (yr) => (
                  <option key={yr} value={yr}>
                    {buddhistFiscalYear(yr)}
                  </option>
                ),
              )}
            </select>
          </label>
        ) : period === 'trimester' ? (
          <div className="flex flex-wrap gap-4">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              ปี
              <select
                className="max-w-[140px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
                value={reportingMonthToFiscalYearQuarter(effectiveReportingMonth).fiscalYear}
                onChange={(e) => {
                  const { quarter } = reportingMonthToFiscalYearQuarter(effectiveReportingMonth);
                  const next = fiscalYearQuarterToAnchorReportingMonth(Number(e.target.value), quarter);
                  setSelectedMonth(next);
                  sharedFilters?.setReportingMonth(next);
                  onReportingMonthChange?.(next);
                }}
              >
                {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map(
                  (yr) => (
                    <option key={yr} value={yr}>
                      {buddhistFiscalYear(yr)}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              ไตรมาส
              <select
                className="max-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
                value={reportingMonthToFiscalYearQuarter(effectiveReportingMonth).quarter}
                onChange={(e) => {
                  const q = Number(e.target.value) as FiscalQuarter;
                  const { fiscalYear } = reportingMonthToFiscalYearQuarter(effectiveReportingMonth);
                  const next = fiscalYearQuarterToAnchorReportingMonth(fiscalYear, q);
                  setSelectedMonth(next);
                  sharedFilters?.setReportingMonth(next);
                  onReportingMonthChange?.(next);
                }}
              >
                {([1, 2, 3, 4] as const).map((q) => (
                  <option key={q} value={q}>
                    {fiscalQuarterOptionLabel(
                      reportingMonthToFiscalYearQuarter(effectiveReportingMonth).fiscalYear,
                      q,
                    )}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            เดือนรายงาน
            <input
              type="month"
              className="max-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800"
              value={effectiveReportingMonth}
              onChange={(e) => {
                const next = e.target.value as ReportingMonth;
                setSelectedMonth(next);
                sharedFilters?.setReportingMonth(next);
                onReportingMonthChange?.(next);
              }}
            />
          </label>
        )}

        {exporting && (
          <div
            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div
              className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"
              aria-hidden
            />
            {NIDATA_LOADING_LABEL}
          </div>
        )}

        {error && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </div>
        )}

        {slideBudgetHint ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
            {slideBudgetHint}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            aria-busy={exporting}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'กำลังส่งออก...' : 'ส่งออก'}
          </button>
        </div>

        <p className="sr-only" aria-live="polite">
          {exporting ? 'กำลังส่งออกไฟล์' : ''}
        </p>

        {mountDashboardClone && (
          <div
            ref={dashboardRef}
            data-export-hub-dashboard-clone
            className="fixed left-0 top-0 z-[-1] w-[1440px] max-w-[1440px] overflow-auto bg-white p-6"
            style={{ visibility: 'hidden', pointerEvents: 'none' }}
            aria-hidden="true"
          >
            <Suspense
              fallback={
                <div className="flex h-[600px] items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                </div>
              }
            >
              {isKpoa ? (
                <KpoaIncidentDashboard
                  useSharedFilters={false}
                  departmentIds={scopedExportDepartmentIds.includes('*') ? ['*'] : scopedExportDepartmentIds}
                />
              ) : isHr ? (
                <HrImportDashboard />
              ) : (
                <Dashboard
                  roleScope={roleScope ?? undefined}
                  roleContext={roleContext ?? undefined}
                  reportingScope={effectiveReportingScope}
                  preselectedCategoryId={effectiveCategoryId}
                  onPreselectConsumed={() => {}}
                  hideInternalFilters={false}
                  initialReportingMonth={effectiveReportingMonth}
                  initialView={period}
                />
              )}
            </Suspense>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportHub;
